import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useContext, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  ADD_MARITIME_DIRECTORY_PORTS,
  AddMaritimeDirectoryPortInput,
  MaritimeDirectoryPortsResponse
} from '../../api/graphql/maritimeDirectoryPort/maritimeDirectoryPort.mutations';
import MaritimeDirectoryPortEditorContext from './MaritimeDirectoryPortEditorContext';
import { useApolloErrorHandler } from 'hooks';
import { portModel } from 'model';
import capitalizeEveryWord from '../../../../../shared/stringUtils/capitalizeEveryWord';
import { TXT_ADD_PORTS, TXT_CLOSE, TXT_SAVE, TXT_SAVING } from '../../../../../shared/translations';
import { PortSelector } from 'components';

interface AddMaritimeDirectoryEditorProps {
  excludeIds?: string[];
  onClose: () => void;
}

const AddMaritimeDirectoryEditor = (props: AddMaritimeDirectoryEditorProps) => {
  const { t } = useTranslation();
  const { apolloErrorHandler } = useApolloErrorHandler();
  const [selectedPorts, setSelectedPorts] = useState<portModel[] | null>(null);
  const { refresh, maritimeDirectoryId } = useContext(MaritimeDirectoryPortEditorContext);

  const [save, { loading }] = useMutation<MaritimeDirectoryPortsResponse>(ADD_MARITIME_DIRECTORY_PORTS, {
    onError: apolloErrorHandler,
    onCompleted: () => {
      refresh();
      props.onClose();
    }
  });

  const onSave = () => {
    if (!selectedPorts || selectedPorts.length === 0) return;

    const input = selectedPorts.map((x): AddMaritimeDirectoryPortInput => {
      return {
        maritimeDirectoryId,
        portId: x.id!,
        isActive: true
      };
    });

    save({ variables: { input } });
  };

  return (
    <Dialog open maxWidth="md" fullWidth>
      <DialogTitle>{capitalizeEveryWord(t(TXT_ADD_PORTS))}</DialogTitle>
      <DialogContent>
        <PortSelector excludePortIds={props.excludeIds} multiple onSelectedPortsChange={setSelectedPorts} />
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} color="secondary" disabled={loading}>
          {t(TXT_CLOSE)}
        </Button>
        <Button
          onClick={onSave}
          color="primary"
          disabled={loading || !selectedPorts || selectedPorts.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {t(loading ? TXT_SAVING : TXT_SAVE)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMaritimeDirectoryEditor;
